import { ActivityLogEvent } from './activity-log.repository';

// One roster row produced by `aggregateAttendance`. Each row maps to a unique
// (scope, userId, breakoutRoom) tuple — i.e. one row per user for room
// presence, plus one row per (user × breakout) for breakout presence.
export type AttendanceRow = {
  userId: string;
  name: string;
  avatar: string;
  scope: 'room' | 'breakout';
  // Present iff `scope === 'breakout'`.
  breakoutRoom?: string;
  firstJoin: Date;
  // `null` ⇒ currently present (latest event is `'joined'`). Otherwise this is
  // the time of the most recent `'left'` event.
  lastLeft: Date | null;
};

// Collapses a flat event log into one row per (scope, userId, breakoutRoom)
// tuple. Latest event wins for display name / avatar so renames mid-session
// surface in the roster. Rows with no `'joined'` event are skipped defensively
// (shouldn't happen in practice — every user enters via a `'joined'`).
export function aggregateAttendance(events: ActivityLogEvent[]): AttendanceRow[] {
  const groups = new Map<string, ActivityLogEvent[]>();
  for (const event of events) {
    const key = `${event.scope}|${event.userId}|${event.breakoutRoom ?? ''}`;
    let bucket = groups.get(key);
    if (!bucket) {
      bucket = [];
      groups.set(key, bucket);
    }
    bucket.push(event);
  }

  const rows: AttendanceRow[] = [];
  for (const bucket of groups.values()) {
    const sorted = bucket.slice().sort((a, b) => a.time.getTime() - b.time.getTime());
    const firstJoinEvent = sorted.find((e) => e.kind === 'joined');
    if (!firstJoinEvent) continue;
    const latest = sorted[sorted.length - 1];
    const lastLeft = latest.kind === 'left' ? latest.time : null;
    rows.push({
      userId: latest.userId,
      name: latest.name,
      avatar: latest.avatar,
      scope: latest.scope,
      breakoutRoom: latest.breakoutRoom,
      firstJoin: firstJoinEvent.time,
      lastLeft,
    });
  }

  rows.sort((a, b) => a.firstJoin.getTime() - b.firstJoin.getTime());
  return rows;
}
